import { alpha, Box, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

type StatusPageLayoutProps = {
  code: string;
  title: string;
  description: string;
  actions: ReactNode;
  children?: ReactNode;
};

export const StatusPageLayout = ({
  actions,
  children,
  code,
  description,
  title,
}: StatusPageLayoutProps) => (
  <Box
    component="main"
    sx={{
      alignItems: "center",
      boxSizing: "border-box",
      background: (theme) =>
        `radial-gradient(ellipse at 50% 0%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 60%), #fbfbfd`,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      minHeight: "100vh",
      px: 3,
      py: 8,
    }}
  >
    <Box
      component="img"
      src="/pimo-large.svg"
      alt="Pimo"
      sx={{ height: 30, left: 32, position: "absolute", top: 28 }}
    />
    <Stack
      alignItems="center"
      spacing={2}
      sx={{
        "@keyframes status-page-rise": {
          from: { opacity: 0, transform: "translateY(12px)" },
          to: { opacity: 1, transform: "none" },
        },
        "@media (prefers-reduced-motion: no-preference)": {
          animation: "status-page-rise 600ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        },
        maxWidth: 520,
      }}
    >
      <Typography
        component="p"
        aria-hidden="true"
        sx={{
          background: (theme) =>
            `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.35)} 100%)`,
          backgroundClip: "text",
          color: "transparent",
          fontSize: { xs: 120, sm: 176 },
          fontWeight: 300,
          letterSpacing: "-0.06em",
          lineHeight: 1,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {code}
      </Typography>
      <Typography
        component="h1"
        sx={{
          color: "primary.main",
          fontSize: { xs: 28, sm: 34 },
          fontWeight: 600,
          letterSpacing: "-0.02em",
          lineHeight: 1.15,
          textAlign: "center",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          color: "#4a5568",
          fontSize: 17,
          lineHeight: 1.5,
          textAlign: "center",
        }}
      >
        {description}
      </Typography>
      {children}
      <Stack
        alignItems="center"
        direction="row"
        flexWrap="wrap"
        gap={1.5}
        justifyContent="center"
        pt={2}
        sx={{
          "& .MuiButton-root": {
            borderRadius: "4px",
            minWidth: "fit-content",
            padding: "7px 16px",
            textTransform: "none",
            whiteSpace: "nowrap",
          },
          "& .MuiButton-contained": { color: "#fff" },
        }}
      >
        {actions}
      </Stack>
    </Stack>
  </Box>
);
